import React, { useEffect, useId, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useUnsavedChanges } from "./UnsavedChangesBoundary";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "../../ui/Button";
import { colors, radius, spacing, typography } from "../../ui/tokens";

const collectFieldErrorMessages = (errors, path = []) => {
  if (!errors || typeof errors !== "object") return [];
  const messages = [];

  Object.entries(errors).forEach(([key, value]) => {
    if (key === "root" || !value) return;

    const nextPath = [...path, key];
    if (Array.isArray(value)) {
      value.forEach((entry, index) => {
        messages.push(...collectFieldErrorMessages(entry, [...nextPath, String(index)]));
      });
      return;
    }

    const message = typeof value?.message === "string" ? value.message.trim() : "";
    if (message) {
      const label = nextPath.join(".");
      messages.push({ field: label, message });
      return;
    }

    messages.push(...collectFieldErrorMessages(value, nextPath));
  });

  return messages;
};

/**
 * Form wrapper with validation support.
 * children can be a function that receives { register, control, formState, setValue, watch }.
 */
export default function FormWithValidation({
  schema,
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  submitDisabled = false,
  children,
  serverFieldErrors = {},
  formError,
  submittingLabel,
  showValidationSummary = false,
  validationSummaryTitle = "Please fix the highlighted fields.",
  formClassName = "",
  fieldsClassName = "",
  actionsClassName = "",
  formStyle,
  fieldsStyle,
  actionsStyle,
  errorStyle,
  summaryStyle,
  summaryTitleStyle,
  summaryListStyle,
  summaryItemStyle,
  disableCancelWhenSubmitting = false,
  cancelDisabled = false,
  customActions,
}) {
  const methods = useForm({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
  });

  const {
    register,
    control,
    handleSubmit,
    formState,
    setError,
    clearErrors,
    reset,
    setValue,
    watch,
  } = methods;
  useUnsavedChanges(formState.isDirty);
  const errorRegionId = useId();
  const lastValuesRef = useRef({});

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    Object.entries(serverFieldErrors || {}).forEach(([field, message]) => {
      setError(field, { type: "server", message });
    });
  }, [serverFieldErrors, setError]);

  useEffect(() => {
    if (formError) {
      setError("root", { type: "server", message: formError });
    } else {
      clearErrors("root");
    }
  }, [formError, setError, clearErrors]);

  const watchedValues = watch();

  useEffect(() => {
    const prev = lastValuesRef.current || {};
    Object.keys(watchedValues || {}).forEach((field) => {
      // Blank valueAsNumber inputs are NaN; unchanged NaN must not trigger a render loop.
      if (!Object.is(prev[field], watchedValues[field])) {
        clearErrors(field);
      }
    });
    lastValuesRef.current = watchedValues;
  }, [watchedValues, clearErrors]);

  const submitHandler = handleSubmit(async (values) => {
    if (onSubmit) {
      await onSubmit(values, methods);
    }
  });

  const rootError = formState?.errors?.root?.message;

  const resolvedSubmitDisabled =
    typeof submitDisabled === "function" ? submitDisabled(formState) : submitDisabled;
  const resolvedCancelDisabled =
    typeof cancelDisabled === "function" ? cancelDisabled(formState) : cancelDisabled;
  const submitButtonDisabled = Boolean(resolvedSubmitDisabled) || formState.isSubmitting;
  const cancelButtonDisabled =
    Boolean(resolvedCancelDisabled) || (disableCancelWhenSubmitting && formState.isSubmitting);
  const resolvedSubmitLabel =
    formState.isSubmitting && submittingLabel ? submittingLabel : submitLabel;

  const fieldErrorMessages = useMemo(
    () => collectFieldErrorMessages(formState?.errors),
    [formState?.errors]
  );

  const uniqueFieldErrorMessages = useMemo(() => {
    const seen = new Set();
    return fieldErrorMessages.filter((entry) => {
      const key = `${entry.field}:${entry.message}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [fieldErrorMessages]);

  const hasValidationSummary = showValidationSummary && uniqueFieldErrorMessages.length > 0;

  return (
    <form
      onSubmit={submitHandler}
      className={`form-with-validation${formClassName ? ` ${formClassName}` : ""}`}
      style={formStyle}
    >
      {rootError && (
        <div
          role="alert"
          aria-live="assertive"
          id={errorRegionId}
          style={{ ...formErrorStyle, ...errorStyle }}
        >
          {rootError.toString()}
        </div>
      )}
      {hasValidationSummary && (
        <div style={{ ...validationSummaryBaseStyle, ...summaryStyle }}>
          <div style={{ ...validationSummaryTitleBaseStyle, ...summaryTitleStyle }}>
            {validationSummaryTitle}
          </div>
          <ul style={{ ...validationSummaryListBaseStyle, ...summaryListStyle }}>
            {uniqueFieldErrorMessages.map((entry) => (
              <li key={`${entry.field}-${entry.message}`} style={{ ...validationSummaryItemBaseStyle, ...summaryItemStyle }}>
                {entry.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className={`form-fields${fieldsClassName ? ` ${fieldsClassName}` : ""}`}
        style={fieldsStyle}
      >
        {typeof children === "function"
          ? children({ register, control, formState, setValue, watch })
          : children}
      </div>
      <div
        className={`form-actions${actionsClassName ? ` ${actionsClassName}` : ""}`}
        style={{ ...formActionsBaseStyle, ...actionsStyle }}
      >
        {customActions ? (
          customActions
        ) : (
          <>
            <Button type="submit" variant="primary" disabled={submitButtonDisabled}>
              {resolvedSubmitLabel}
            </Button>
            {onCancel && (
              <Button type="button" variant="secondary" onClick={onCancel} disabled={cancelButtonDisabled}>
                {cancelLabel}
              </Button>
            )}
          </>
        )}
      </div>
    </form>
  );
}

const formErrorStyle = {
  color: colors.danger,
  background: colors.dangerSubtle,
  border: `1px solid ${colors.dangerBorder}`,
  borderRadius: radius.sm,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.xs,
};

const validationSummaryBaseStyle = {
  border: `1px solid ${colors.dangerBorder}`,
  background: colors.dangerSubtle,
  borderRadius: radius.sm,
  padding: `${spacing.sm}px ${spacing.md}px`,
  display: "grid",
  gap: spacing.xs,
};

const validationSummaryTitleBaseStyle = {
  color: colors.danger,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
};

const validationSummaryListBaseStyle = {
  margin: 0,
  paddingLeft: spacing.lg + spacing.sm,
  display: "grid",
  gap: spacing.xs / 2,
};

const validationSummaryItemBaseStyle = {
  color: colors.danger,
  fontSize: typography.size.xs,
};

const formActionsBaseStyle = {
  display: "flex",
  gap: spacing.sm,
  rowGap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: spacing.sm,
  paddingTop: spacing.md,
  borderTop: `1px solid ${colors.border}`,
};
